import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { useLocale, t } from '../hooks/useT';

interface Entry {
  rank: number;
  displayName: string;
  score: number;
  dezoomsUsed: number;
  won: boolean;
  isMe: boolean;
}

function formatArchiveDate(dateStr: string, locale: string) {
  const [y, m, d] = dateStr.slice(0, 10).split('-').map(Number);
  return new Intl.DateTimeFormat(locale === 'en' ? 'en-GB' : 'fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(y, m - 1, d));
}

export function ArchiveLeaderboardPage() {
  const { puzzleNumber } = useParams<{ puzzleNumber: string }>();
  const { pathname } = useLocation();
  const friendsScope = pathname.endsWith('/amis');
  const { user, guest } = useAuth();
  const { locale } = useLocale();
  const [entries, setEntries] = useState<Entry[]>([]);
  const [myRank, setMyRank] = useState<number | null>(null);
  const [myScore, setMyScore] = useState<number | null>(null);
  const [myPlayed, setMyPlayed] = useState(false);
  const [myWon, setMyWon] = useState(false);
  const [playersCount, setPlayersCount] = useState(0);
  const [num, setNum] = useState(0);
  const [publishDate, setPublishDate] = useState('');
  const [fetchError, setFetchError] = useState('');

  const basePath = `/archives/${puzzleNumber}/leaderboard`;
  const apiPath = friendsScope
    ? `/leaderboard/friends/puzzle/${puzzleNumber}`
    : `/leaderboard/puzzle/${puzzleNumber}`;

  useEffect(() => {
    if (!puzzleNumber) return;
    if (friendsScope && (!user || guest)) {
      setEntries([]);
      setFetchError('');
      return;
    }
    setFetchError('');
    api<{
      entries: Entry[];
      myRank: number | null;
      myScore: number | null;
      myPlayed: boolean;
      myWon: boolean;
      playersCount: number;
      puzzleNumber: number;
      publishDate: string;
    }>(apiPath)
      .then((data) => {
        setEntries(data.entries);
        setMyRank(data.myRank);
        setMyScore(data.myScore);
        setMyPlayed(data.myPlayed);
        setMyWon(data.myWon);
        setPlayersCount(data.playersCount);
        setNum(data.puzzleNumber);
        setPublishDate(data.publishDate);
      })
      .catch(() => {
        setEntries([]);
        if (friendsScope) setFetchError(t('leaderboard.friendsEmpty'));
      });
  }, [puzzleNumber, friendsScope, user, guest, apiPath]);

  if (friendsScope && (!user || guest)) {
    return (
      <main className="page leaderboard">
        <Link to="/archives" className="link-muted">← {t('archives.title')}</Link>
        <h1>{t('leaderboard.scopeFriends')}</h1>
        <p className="muted">{t('friends.loginPrompt')}</p>
        <Link to="/login" className="btn primary">{t('landing.login')}</Link>
      </main>
    );
  }

  return (
    <main className="page leaderboard">
      <Link to="/archives" className="link-muted">← {t('archives.title')}</Link>
      <h1>{friendsScope ? t('leaderboard.scopeFriends') : t('leaderboard.archiveTitle')}</h1>
      <p className="sub">
        {t('archives.puzzleLabel')} #{num}
        {publishDate && ` — ${formatArchiveDate(publishDate, locale)}`}
      </p>

      <nav className="lb-scope-tabs" aria-label={t('leaderboard.scopeNav')}>
        <Link
          to={basePath}
          className={!friendsScope ? 'lb-period-tab active' : 'lb-period-tab'}
        >
          {t('leaderboard.scopeGlobal')}
        </Link>
        <Link
          to={`${basePath}/amis`}
          className={friendsScope ? 'lb-period-tab active' : 'lb-period-tab'}
        >
          {t('leaderboard.scopeFriends')}
        </Link>
      </nav>

      {playersCount > 0 && (
        <p className="lb-players-count">
          {playersCount} {playersCount > 1 ? t('leaderboard.players') : t('leaderboard.player')}
        </p>
      )}
      {user && myPlayed && myWon && myRank != null && (
        <div className="my-stats-row">
          <p className="my-rank">{t('leaderboard.myRank')} #{myRank}</p>
        </div>
      )}
      {user && myPlayed && !myWon && (
        <p className="my-rank my-rank--lost">
          {t('leaderboard.myPlayed')} — {myScore ?? 0} pt
        </p>
      )}
      {fetchError && <p className="muted">{fetchError}</p>}
      <ol className="lb-list">
        {entries.map((e) => (
          <li key={`${e.rank}-${e.displayName}`} className={`${e.isMe ? 'me' : ''}${!e.won ? ' lost' : ''}`}>
            <span className="rank">#{e.rank}</span>
            <span className="name">{e.displayName}</span>
            <span className="pts">{e.score}</span>
          </li>
        ))}
      </ol>
      {!entries.length && !fetchError && (
        <p className="muted">{friendsScope ? t('leaderboard.friendsEmpty') : t('leaderboard.empty')}</p>
      )}
      {playersCount > entries.length && entries.length > 0 && (
        <p className="muted lb-top-hint">{t('leaderboard.top100Hint')}</p>
      )}
      <div className="archives-lb-actions">
        <Link
          to={myPlayed ? `/archives/${puzzleNumber}/play?replay=1` : `/archives/${puzzleNumber}/play`}
          className="btn primary"
        >
          {myPlayed ? t('archives.replay') : t('archives.play')}
        </Link>
        <Link to="/friends" className="btn secondary">{t('friends.title')}</Link>
      </div>
    </main>
  );
}
